import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  interval,
  Observable,
  Subject,
  switchMap,
  takeUntil,
  takeWhile,
  timer
} from 'rxjs';

import {
  OpportunityWorkflowDisplayState,
  OpportunityWorkflowStatus
} from '@shared/models/opportunity';
import { CustomisationService } from '@shared/services/customisation/customisation.service';

import { OpportunityEventService } from './opportunity-event.service';

export type PollingResult = {
  submissionComplete: boolean;
  workflowDisplayState: OpportunityWorkflowDisplayState | undefined;
  timedOut: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class SubmissionStatusService {
  constructor(
    private router: Router,
    private customisationService: CustomisationService,
    private opportunityEventService: OpportunityEventService
  ) {}

  submissionIsComplete(workflowStatus: OpportunityWorkflowStatus): boolean {
    const unsubmittedStatuses = [
      OpportunityWorkflowStatus.Draft,
      OpportunityWorkflowStatus.ReadyToSubmit,
      OpportunityWorkflowStatus.Submitting,
      OpportunityWorkflowStatus.SubmissionFailed
    ];
    
    return !unsubmittedStatuses.includes(workflowStatus);
  }

  handleSubmissionComplete(
    opportunityId: string,
    workflowDisplayState: OpportunityWorkflowDisplayState
  ): void {
    const settings = this.customisationService.getCustomisation();

    if (settings.submission.emitSubmissionEvent) {
      // For LFAU, emit event for React to handle
      const event = new CustomEvent('submissionComplete', {
        bubbles: true, // Ensures the event propagates from the web component to the parent
        detail: {
          opportunityId,
          loanId: workflowDisplayState.libertyLoanId
        }
      });
      window.dispatchEvent(event);
    }
  }

  startPolling(opportunityId: string): Observable<PollingResult> {
    const result$ = new Subject<PollingResult>();
    
    const polling$ = interval(3000).pipe(
      switchMap(() => {
        this.opportunityEventService.refreshData();
        return this.opportunityEventService.selectedOpportunitySummary$;
      }),
      takeWhile(os => !this.submissionIsComplete(os.summary.workflowDisplayState.status), true),  // poll until submission successful
      takeUntil(timer(120000)) // or until two minutes (timeout)
    );
  
    let subscription: any;
    subscription = polling$.subscribe({
      next: (os) => {
        const workflowDisplayState = os.summary.workflowDisplayState;
        const pollingResult: PollingResult = {
          submissionComplete: this.submissionIsComplete(workflowDisplayState.status),
          workflowDisplayState,
          timedOut: false
        };
  
        // Emit the result
        result$.next(pollingResult);
  
        // If submission is complete, stop polling
        if (pollingResult.submissionComplete) {
          subscription.unsubscribe();
          result$.complete();
        }
      },
      error: (err) => {
        // On error, continue polling
        result$.next({
          submissionComplete: false,
          workflowDisplayState: undefined,
          timedOut: false
        });
      },
      complete: () => {
        // If we complete (due to timeout) and haven't already completed due to success
        if (!result$.closed) {
          result$.next({
            submissionComplete: false,
            workflowDisplayState: undefined,
            timedOut: true
          });
          result$.complete();
        }
      }
    });
  
    return result$.asObservable();
  }

  shouldShowSubmissionModal(): boolean {
    return this.customisationService.getCustomisation().submission
      .showSubmissionModal;
  }
}
